/** Types for the in-app assistant (DeviceRegistry.Api `/api/assistant/*`) and its settings. */

export type AssistantScope = 'tenant' | 'serviceDesk';

export interface AssistantModelChoice {
    id: string;
    label: string;
}

/** GET/PUT /api/namespaces/{id}/assistant and /api/admin/assistant. The key itself is never returned. */
export interface AssistantSettings {
    enabled: boolean;
    configured: boolean;
    keyHint: string | null;
    model: string;
    updatedUtc: string | null;
    updatedBy: string | null;
    lastRejectedUtc: string | null;
    encryptionAvailable: boolean;
    models: AssistantModelChoice[];
    /** Last 30 days; tenant settings only. */
    usage?: { conversations: number; inputTokens: number; outputTokens: number } | null;
}

export interface UpdateAssistantSettings {
    enabled?: boolean;
    apiKey?: string;
    model?: string;
}

/** Cross-tenant assistant access, granted by a superadmin. */
export interface ServiceDeskGrant {
    allNamespaces: boolean;
    namespaceIds: string[];
    canProposeActions: boolean;
    grantedBy?: string;
    grantedUtc?: string;
    expiresUtc: string | null;
}

/** GET /api/admin/service-desk — one person with service desk access (an explicit grant, or a superadmin's implicit one). */
export interface ServiceDeskMember {
    userId: string;
    displayName: string | null;
    email: string | null;
    superAdmin: boolean;
    disabled: boolean;
    status: 'active' | 'expired' | 'superadmin' | 'disabled';
    grant: ServiceDeskGrant | null;
    tenants: { id: string; name: string }[];
    grantedByName: string | null;
}

export interface AssistantStatus {
    tenantEnabled: boolean;
    canProposeActions: boolean;
    /** May draft GitHub issues for this tenant (`issues.manage`). */
    canProposeIssues?: boolean;
    serviceDesk: { granted: boolean; available: boolean; canProposeActions: boolean; tenantCount: number };
}

export type AssistantTone = 'danger' | 'warn' | 'success' | 'info';

export interface AssistantPulseStat {
    key: string;
    label: string;
    value: number;
    tone: AssistantTone;
    prompt: string;
}

export interface AssistantPulse {
    scope: 'Tenant' | 'ServiceDesk';
    generatedUtc: string;
    headline: string;
    stats: AssistantPulseStat[];
    prompts: string[];
    tenants: AssistantPulseTenant[];
}

/** A tenant on the service desk overview, worst first. Problem counts are distinct log problems in the last 24 hours. */
export interface AssistantPulseTenant {
    namespaceId: string;
    name: string;
    devices: number;
    offline: number;
    attentionScore: number;
    warnings?: number;
    errors?: number;
    critical?: number;
    neverCheckedIn?: number;
    checkInIssues?: number;
}

export interface AssistantConversationSummary {
    id: string;
    title: string;
    scope: 'Tenant' | 'ServiceDesk' | string;
    namespaceId: string | null;
    createdUtc: string;
    updatedUtc: string;
}

/** `Confirming`: a confirm has claimed it and is queueing the device action right now. */
export type PendingActionStatus = 'Proposed' | 'Confirming' | 'Confirmed' | 'Rejected' | 'Expired' | 'Failed';

export interface AssistantPendingAction {
    id: string;
    deviceId: string;
    deviceName: string;
    namespaceId: string;
    action: 'Reboot' | 'Restart' | 'Cancel' | string;
    targetActionId: string | null;
    reason: string;
    status: PendingActionStatus;
    createdUtc: string;
    expiresUtc: string;
    deviceActionId: string | null;
    error: string | null;
}

export interface DeviceCard {
    deviceId: string;
    namespaceId: string | null;
    namespaceName: string | null;
    name: string;
    status: string;
    offline: boolean;
    lastCheckInUtc: string | null;
    model: string | null;
    firmwareVersion: string | null;
    ipAddress: string | null;
    locationId: string | null;
    locationName: string | null;
}

export interface DevicesBlock {
    type: 'devices';
    title: string | null;
    canAct: boolean;
    devices: DeviceCard[];
}

export interface RoomTile {
    locationId: string;
    namespaceId: string;
    namespaceName: string | null;
    name: string;
    type: string | null;
    rating: number | null;
    ratingCount: number;
    devices: number;
    offline: number;
}

export interface RoomsBlock {
    type: 'rooms';
    title: string | null;
    rooms: RoomTile[];
}

export interface ChartBlock {
    type: 'chart';
    kind: 'line' | 'bar';
    title: string | null;
    unit: string | null;
    labels: string[];
    series: { name: string; values: (number | null)[] }[];
}

export interface LogsBlock {
    type: 'logs';
    deviceId: string;
    deviceName: string;
    namespaceId: string;
    title: string | null;
    entries: { timestampUtc: string | null; level: string; message: string }[];
}

export type TimelineKind = 'info' | 'warning' | 'error' | 'success' | 'action';

export interface TimelineBlock {
    type: 'timeline';
    title: string | null;
    events: { timeUtc: string | null; label: string; kind: TimelineKind }[];
}

export interface ActionBlock {
    type: 'action';
    action: AssistantPendingAction;
}

/** An issue shown next to a draft: one that tracks the same problem (`tracking`), or reads alike. */
export interface AssistantIssueRef {
    number: number;
    title: string;
    state: 'open' | 'closed' | string;
    htmlUrl: string;
    updatedUtc: string;
    tracking: boolean;
}

/** `Issue`: a new issue; `Comment`: the evidence added to an issue that is already open; `Update`: reopen, close, retitle or relabel one. */
export type AssistantIssueKind = 'Issue' | 'Comment' | 'Update';

/** A GitHub issue (or comment) the assistant drafted. Nothing reaches GitHub until the person who asked confirms it. */
export interface AssistantIssueProposal {
    id: string;
    namespaceId: string;
    kind: AssistantIssueKind | string;
    repository: string;
    repositoryUrl: string;
    packageName?: string | null;
    issueNumber?: number | null;
    issueTitle?: string | null;
    /** For a comment or an update: the issue's state when proposed. */
    issueState?: string | null;
    /** For an update: `open` (reopen) or `closed`, or absent to leave the state. */
    newState?: 'open' | 'closed' | null;
    /** `completed` or `not_planned` when closing; `reopened` when reopening. */
    stateReason?: string | null;
    /** For an update: labels to take off. `labels` are the ones to add. */
    removeLabels?: string[];
    title: string;
    body: string;
    labels: string[];
    /** `proav:problem/{signature}`, added on filing so later duplicate checks find it. */
    problemLabel?: string | null;
    /** Written by the API from stored data; shown read-only. */
    evidence?: string | null;
    problemId?: string | null;
    problemSummary?: string | null;
    affectedDevices: number;
    reason: string;
    related: AssistantIssueRef[];
    status: PendingActionStatus;
    createdUtc: string;
    expiresUtc: string;
    resultNumber?: number | null;
    resultUrl?: string | null;
    error?: string | null;
    serviceDesk: boolean;
}

/** The person's last edits, sent with the confirm. `commentOn` adds it to that open issue instead. */
export interface ConfirmIssueProposal {
    title?: string;
    body?: string;
    labels?: string[];
    commentOn?: number;
}

export interface IssueProposalBlock {
    type: 'issueProposal';
    proposal: AssistantIssueProposal;
}

/** One live GitHub issue card, read from GitHub by the API when drawn. */
export interface IssueCard {
    repository: string;
    number: number;
    title: string;
    state: 'open' | 'closed' | string;
    stateReason?: string | null;
    htmlUrl: string;
    updatedUtc: string;
    commentsCount: number;
    author?: string | null;
    packageName?: string | null;
    assignees: string[];
    labels: { name: string; color?: string | null }[];
    /** Carries a `proav:problem/…` label: it tracks a log problem. */
    problem: boolean;
    roomPath?: string | null;
}

export interface IssuesBlock {
    type: 'issues';
    title: string | null;
    namespaceId: string;
    issues: IssueCard[];
}

export type AssistantBlock = DevicesBlock | RoomsBlock | ChartBlock | LogsBlock | TimelineBlock | ActionBlock | IssueProposalBlock | IssuesBlock;

export type DisplayKind = 'text' | 'progress' | 'tool' | 'block' | 'suggestions' | 'error';

/** One thing drawn for a reply. `toolId`/`toolStatus` exist only while a reply is streaming. */
export interface AssistantDisplayItem {
    kind: DisplayKind;
    text?: string | null;
    toolName?: string | null;
    toolFailed?: boolean | null;
    toolId?: string;
    toolStatus?: 'running' | 'done' | 'error';
    block?: AssistantBlock | null;
    suggestions?: string[] | null;
    /** Error code from the stream (`rate_limited`, `key_rejected`, …). */
    code?: string;
}

export interface AssistantMessage {
    id: string;
    role: 'user' | 'assistant';
    createdUtc: string;
    items: AssistantDisplayItem[];
    streaming?: boolean;
}

export interface AssistantConversationDetail {
    conversation: AssistantConversationSummary;
    messages: AssistantMessage[];
    actions: Record<string, AssistantPendingAction>;
    issueProposals?: Record<string, AssistantIssueProposal>;
}

export interface AssistantMention {
    type: 'device' | 'location';
    id: string;
    label: string;
}

export interface AssistantPageContext {
    url: string | null;
    route: string | null;
    pageTitle: string | null;
    deviceId: string | null;
    locationId: string | null;
    mentions: AssistantMention[];
}

/** Server-sent event from POST /api/assistant/conversations/{id}/messages. */
export type AssistantStreamEvent =
    | { type: 'conversation'; data: { id: string; title: string } }
    | { type: 'text'; data: { text: string } }
    | { type: 'progress'; data: { text: string } }
    | { type: 'tool'; data: { id: string; name: string; status: 'running' | 'done' | 'error' } }
    | { type: 'block'; data: { block: AssistantBlock } }
    | { type: 'suggestions'; data: { items: string[] } }
    | { type: 'error'; data: { code: string; message: string } }
    | { type: 'done'; data: { usage?: { inputTokens: number; outputTokens: number; cacheReadInputTokens: number } } };

/** A rendered run of a reply: consecutive progress notes and tool chips collapse into one step list. */
export type ReplySegment =
    { kind: 'text'; text: string } | { kind: 'steps'; steps: AssistantDisplayItem[]; running: boolean } | { kind: 'block'; block: AssistantBlock } | { kind: 'suggestions'; suggestions: string[] } | { kind: 'error'; text: string; code?: string };

export function segmentsOf(items: AssistantDisplayItem[], streaming = false): ReplySegment[] {
    const out: ReplySegment[] = [];
    for (const item of items) {
        const last = out[out.length - 1];
        switch (item.kind) {
            case 'text':
                if (!item.text) break;
                if (last?.kind === 'text') last.text += item.text;
                else out.push({ kind: 'text', text: item.text });
                break;
            case 'progress':
            case 'tool':
                if (last?.kind === 'steps') last.steps.push(item);
                else out.push({ kind: 'steps', steps: [item], running: false });
                break;
            case 'block':
                if (item.block) out.push({ kind: 'block', block: item.block });
                break;
            case 'suggestions':
                if (item.suggestions?.length) out.push({ kind: 'suggestions', suggestions: item.suggestions });
                break;
            case 'error':
                out.push({ kind: 'error', text: item.text ?? '', code: item.code });
                break;
        }
    }
    if (streaming) {
        const last = out[out.length - 1];
        if (last?.kind === 'steps') last.running = true;
        return out;
    }
    return promoteFinalNote(out);
}

/**
 * A finished reply should never end with its answer folded away in a collapsed step list. If a reply has no
 * answer text (the model wrote it as a progress note before a final tool call), the last note becomes the answer.
 */
function promoteFinalNote(out: ReplySegment[]): ReplySegment[] {
    if (out.some((s) => s.kind === 'text' || s.kind === 'error')) return out;
    for (let i = out.length - 1; i >= 0; i--) {
        const seg = out[i];
        if (seg.kind !== 'steps') continue;
        const noteIndex = seg.steps.map((s) => s.kind).lastIndexOf('progress');
        if (noteIndex < 0) continue;
        const note = seg.steps[noteIndex].text ?? '';
        const steps = seg.steps.filter((_, j) => j !== noteIndex);
        const replacement: ReplySegment[] = steps.length ? [{ ...seg, steps }] : [];
        // The answer goes after the step list and any cards, just before the suggestions.
        const rest = [...out.slice(0, i), ...replacement, ...out.slice(i + 1)];
        const at = rest.findIndex((s) => s.kind === 'suggestions');
        rest.splice(at < 0 ? rest.length : at, 0, { kind: 'text', text: note });
        return rest;
    }
    return out;
}

/** What a reply in progress is doing right now, for the live status line. */
export interface ReplyStatus {
    verb: string;
    detail: string | null;
}

const TOOL_ACTIVITY: Record<string, string> = {
    fleet_health_summary: 'Checking fleet health',
    platform_overview: 'Surveying every tenant',
    cross_tenant_triage: 'Looking for problems across tenants',
    triage_problems: 'Triaging problems',
    room_feedback: 'Reading room feedback',
    room_energy: 'Checking energy use',
    room_utilisation: 'Checking how rooms are used',
    device_timeline: 'Building the device timeline',
    device_software: 'Checking software versions',
    device_system_metrics: 'Checking processor load',
    fleet_resource_hotspots: 'Looking for busy processors',
    firmware_status: 'Checking firmware',
    get_live_config: 'Reading the live config',
    propose_device_action: 'Preparing an action for you to confirm',
    propose_cancel_device_action: 'Preparing an action for you to confirm',
    find_related_issues: 'Checking GitHub for existing issues',
    show_issues: 'Reading the issues on GitHub',
    propose_github_issue: 'Drafting a GitHub issue for you to check',
    propose_issue_comment: 'Drafting a comment for you to check',
    propose_issue_update: 'Drafting an issue change for you to check'
};

const TOOL_VERBS: Record<string, string> = { list: 'Listing', get: 'Reading', search: 'Searching', diff: 'Comparing', compare: 'Comparing' };

/** `search_device_logs` → "Searching device logs"; well-known tools get a hand-written phrase. */
export function toolActivity(name: string | null | undefined): string {
    if (!name) return 'Working';
    if (TOOL_ACTIVITY[name]) return TOOL_ACTIVITY[name];
    const [first, ...rest] = name.split('_').filter(Boolean);
    const verb = TOOL_VERBS[first];
    return verb ? [verb, ...rest].join(' ') : `Checking ${[first, ...rest].join(' ')}`;
}

export function replyStatus(items: AssistantDisplayItem[]): ReplyStatus {
    const running = [...items].reverse().find((i) => i.kind === 'tool' && i.toolStatus === 'running');
    if (running) return { verb: toolActivity(running.toolName), detail: null };
    const last = items[items.length - 1];
    if (last?.kind === 'text') return { verb: 'Writing', detail: null };
    if (last?.kind === 'progress' && last.text) {
        const sentences = last.text.trim().split(/(?<=[.!?])\s+/);
        const latest = sentences[sentences.length - 1] || last.text;
        return { verb: 'Thinking', detail: latest.length > 90 ? latest.slice(0, 89).trimEnd() + '…' : latest };
    }
    if (last?.kind === 'block') return { verb: 'Drawing', detail: null };
    return { verb: 'Thinking', detail: null };
}

/** The slash commands of the composer, expanded to the prompt they send. */
export interface SlashCommand {
    command: string;
    description: string;
    /** Prompt to send; null for local commands (/new, /clear). `{arg}` is replaced by the text after the command. */
    prompt: string | null;
}

export const SLASH_COMMANDS: SlashCommand[] = [
    { command: '/offline', description: 'Which devices are offline and why', prompt: 'Which devices are offline right now, and why?' },
    { command: '/issues', description: 'Triage today’s problems', prompt: 'Triage the problems devices have logged in the last 24 hours — what should I fix first?' },
    {
        command: '/raise',
        description: 'Raise untracked problems with the developers',
        prompt: 'Which recurring problems this week need a developer and aren’t tracked by a GitHub issue yet? Check for existing issues first, then draft issues for the important ones.'
    },
    {
        command: '/tidy',
        description: 'Close or reopen issues to match what devices report',
        prompt: 'Compare our open GitHub issues with the problems devices are logging: which tracked problems have stopped (so the issue could be closed) and which closed issues are back? Propose the changes for me to confirm.'
    },
    { command: '/outstanding', description: 'Open GitHub issues on our packages', prompt: 'Show the outstanding GitHub issues on our packages: what is open, how old, and which are linked to problems still happening.' },
    { command: '/rooms', description: 'How users rate the rooms', prompt: 'How are users rating the rooms this month? Highlight the worst and the best.' },
    { command: '/energy', description: 'Energy use and the biggest consumers', prompt: 'Show energy use over the last 7 days and which rooms or devices use the most.' },
    { command: '/summary', description: 'A weekly digest', prompt: 'Give me a summary of the last week: health, problems, room feedback and anything I should act on.' },
    { command: '/device', description: 'Everything about one device', prompt: 'Tell me everything important about the device {arg}: health, recent problems and what changed.' },
    { command: '/new', description: 'Start a new conversation', prompt: null },
    { command: '/clear', description: 'Clear the composer', prompt: null }
];

/** Expands `/command rest` to its prompt; null when the text is not a known prompt command. */
export function expandSlash(text: string): string | null {
    const match = /^(\/[a-z]+)(?:\s+(.*))?$/s.exec(text.trim());
    if (!match) return null;
    const command = SLASH_COMMANDS.find((c) => c.command === match[1]);
    if (!command?.prompt) return null;
    const arg = (match[2] ?? '').trim();
    return command.prompt.includes('{arg}') ? command.prompt.replace('{arg}', arg || 'on this page') : arg ? `${command.prompt} ${arg}` : command.prompt;
}

/**
 * The completion shown as ghost text after what has been typed: the first candidate that starts with
 * the typed text (case-insensitive) and is longer than it. Empty input shows the first candidate.
 */
export function predictCompletion(typed: string, candidates: string[]): string {
    if (typed.includes('\n')) return '';
    const lower = typed.toLowerCase();
    for (const candidate of candidates) {
        if (!candidate) continue;
        if (typed.length === 0) return candidate;
        if (candidate.length > typed.length && candidate.toLowerCase().startsWith(lower)) return candidate.slice(typed.length);
    }
    return '';
}

/** The next word of a ghost completion (with its leading space), for → to accept a word at a time. */
export function nextWord(completion: string): string {
    const match = /^\s*\S+/.exec(completion);
    return match ? match[0] : completion;
}

/** Parses SSE frames from a growing buffer; returns complete events and the unconsumed remainder. */
export function parseSseFrames(buffer: string): { events: { type: string; data: string }[]; rest: string } {
    const events: { type: string; data: string }[] = [];
    const normalised = buffer.replace(/\r\n/g, '\n');
    let start = 0;
    let end: number;
    while ((end = normalised.indexOf('\n\n', start)) !== -1) {
        const frame = normalised.slice(start, end);
        start = end + 2;
        let type = 'message';
        const data: string[] = [];
        for (const line of frame.split('\n')) {
            if (line.startsWith(':')) continue;
            if (line.startsWith('event:')) type = line.slice(6).trim();
            else if (line.startsWith('data:')) data.push(line.slice(5).replace(/^ /, ''));
        }
        if (data.length) events.push({ type, data: data.join('\n') });
    }
    return { events, rest: normalised.slice(start) };
}
